import { LinkType, type MonetizableEnum } from '@ria-client/segment/enums';
import type { AnalyticsType } from '@ria-client/segment/types';
import { EntityPlacementCtaTypeEnum } from '@ria-client/supergraph/marketplace/enums';
import type { CtaType, ImageType } from '@ria-client/supergraph/roundup/types';
import { CtaButton, RatingTooltip, StarRating, TrackedLink } from '@ria-client/tw-shared-ui';
import { Star } from 'lucide-react';
import { RiaDisclaimer } from '../financial-advisors-summary-table/ria-disclaimer';
import { ContentLink } from '../lib/content-link';

export interface ImageCtaReviewSectionProps {
  cta?: CtaType;
  ctaAnalytics: AnalyticsType;
  displayName?: string;
  image?: ImageType;
  isFidelityOffer?: boolean;
  isRiaOffer?: boolean;
  monetizable: MonetizableEnum;
  periodConsideredDate?: string;
  riaDisclaimerText?: string;
  reviewLink?: string;
  reviewedAtDate?: string;
  starRating?: number;
  starRatingTooltip?: string;
}

export function ImageCtaReviewSection({
  cta,
  ctaAnalytics,
  displayName,
  image,
  isFidelityOffer,
  isRiaOffer,
  monetizable,
  periodConsideredDate,
  riaDisclaimerText,
  reviewLink,
  reviewedAtDate,
  starRating,
  starRatingTooltip,
}: ImageCtaReviewSectionProps) {
  const imageNode = image?.source && displayName && (
    <img
      alt={image.alt || displayName}
      className="h-11 w-[140px] object-contain md:h-[75px] md:w-[205px]"
      height={75}
      src={image.source}
      width={205}
    />
  );

  return (
    <div className="mr-0 flex flex-col md:mr-6">
      <div className="mb-3 flex">
        <div>
          <div className="w-[140px] md:w-[205px]">
            {cta ? (
              <TrackedLink
                analyticsPayload={ctaAnalytics}
                cta={{ ...cta, type: EntityPlacementCtaTypeEnum.ProductImage }}
                linkType={LinkType.PRODUCT_IMAGE}
                monetizable={ctaAnalytics.monetizable}
              >
                {imageNode}
              </TrackedLink>
            ) : (
              imageNode
            )}
          </div>
          <div className="hidden max-w-[205px] pt-3 text-center md:block">
            <CtaButton analyticsPayload={ctaAnalytics} cta={cta} />
          </div>
        </div>
        <Rating
          reviewLink={reviewLink}
          isRiaOffer={isRiaOffer}
          periodConsideredDate={periodConsideredDate}
          reviewedAtDate={reviewedAtDate}
          starRating={starRating}
          starRatingTooltip={starRatingTooltip}
        />
      </div>
      <div className="max-w-full text-center md:hidden">
        <CtaButton analyticsPayload={ctaAnalytics} cta={cta} />
      </div>
      {isRiaOffer && riaDisclaimerText ? (
        <div className="mt-2 flex max-w-full justify-center md:mt-0 md:max-w-[236px] md:justify-start">
          <RiaDisclaimer
            isFidelityOffer={isFidelityOffer}
            monetizable={monetizable}
            riaDisclaimerText={riaDisclaimerText}
          />
        </div>
      ) : null}
    </div>
  );
}

interface RatingProps {
  isRiaOffer?: boolean;
  periodConsideredDate?: string;
  reviewLink?: string;
  reviewedAtDate?: string;
  starRating?: number;
  starRatingTooltip?: string;
}

function Rating({
  isRiaOffer,
  periodConsideredDate,
  reviewLink,
  reviewedAtDate,
  starRating,
  starRatingTooltip,
}: RatingProps) {
  return (
    <div className="ml-2 flex flex-col md:ml-4">
      {starRating ? (
        <>
          <div className="inline-block">
            <span className="typography-text-small pr-0.5 text-muted-foreground">
              NerdWallet rating
            </span>
            {starRatingTooltip && <RatingTooltip starRatingTooltip={starRatingTooltip} />}
          </div>
          <div className="mr-0.5 flex items-center text-2xl">
            <span className="w-5 overflow-hidden">
              <StarRating gap="5px" size="20px" value={starRating} />
            </span>
            {reviewLink ? (
              <ContentLink href={reviewLink} rel="noopener" target="_blank">
                {starRating.toFixed(1)}
              </ContentLink>
            ) : (
              starRating.toFixed(1)
            )}
            <span className="typography-text-small pt-2 pl-0.5 text-muted-foreground">/5</span>
          </div>
        </>
      ) : (
        <span className="typography-text-small block text-muted-foreground">
          <Star
            aria-hidden
            className="mr-1 inline fill-border text-border align-bottom"
            size={20}
          />
          Not yet rated
        </span>
      )}
      {isRiaOffer && reviewedAtDate && periodConsideredDate ? (
        <div className="typography-text-small mt-3 flex max-w-full flex-col md:max-w-[180px]">
          <span>{reviewedAtDate}</span>
          <span>{periodConsideredDate}</span>
        </div>
      ) : null}
    </div>
  );
}
